/**
 * useRunsheet — replaces AngularJS RunsheetService + all RunCtrl runsheet logic.
 *
 * Key design decisions vs the original:
 *
 * 1. NO LokiJS.  The original used LokiJS as an in-memory/Cordova-FS cache, but
 *    the real source of truth was always localStorage + the API.  We keep the
 *    same localStorage key ("runsheet") for offline parity.
 *
 * 2. $q promises → async/await.  Every AngularJS $q() wrapper becomes a plain
 *    async function.  Rejection messages are thrown as Error objects.
 *
 * 3. Dirty-line sync.  The original RunsheetService.save() iterated dirty lines
 *    and batched them into PUT /runsheetlines.  That logic is preserved in
 *    saveRunsheet() below.
 *
 * 4. Phone number normalisation from RunsheetService.get() is preserved verbatim.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '@/env';
import type { Runsheet, RunsheetLine, RunsheetTotals } from '@/types';

const STORAGE_KEY = 'runsheet';

export function normalisePhones(line: RunsheetLine): RunsheetLine {
  const fix = (raw: string | number | null | undefined, mobile = false): string => {
    if (!raw) return '';
    let s = String(raw).replace(/^61/, '');
    if (s.length === 9) s = '0' + s;
    if (mobile && !s.startsWith('0')) s = '0' + s;
    return s;
  };
  return {
    ...line,
    ph_mob: fix(line.ph_mob as unknown as string | number, true),
    ph_work: fix(line.ph_work as unknown as string | number),
    ph_home: fix(line.ph_home as unknown as string | number),
  };
}

// Free-text fields that the code treats as strings.
const TEXT_FIELDS = [
  'driver_notes',
  'cont_notes',
  'cust_notes',
  'ubd_map',
  'ubd_c1',
  'ubd_c2',
] as const;

/**
 * The API encodes with JSON_NUMERIC_CHECK, so digit-only phones and notes
 * arrive as numbers and string methods like .replace() throw on them.
 */
export function normaliseLine(line: RunsheetLine): RunsheetLine {
  const out = normalisePhones(line);
  for (const field of TEXT_FIELDS) {
    const value = out[field] as unknown;
    if (typeof value === 'number') out[field] = String(value);
  }
  return out;
}

function normaliseRunsheet(runsheet: Runsheet): Runsheet {
  return { ...runsheet, lines: (runsheet.lines ?? []).map(normaliseLine) };
}

export function calcTotals(lines: RunsheetLine[]): RunsheetTotals {
  const totals: RunsheetTotals = { bags: 0, income: 0, complete: 0, pending: 0 };
  for (const line of lines) {
    if (line.num_bags > 0 && !line.pickupcode.match(/overdue/i)) {
      totals.bags += Number(line.num_bags);
      if (line.driver_locked) totals.complete += 1;
      else totals.pending += 1;
    }
  }
  return totals;
}

function loadLocal(): Runsheet | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    // Normalised on read too, because older caches hold the API's raw numbers.
    return raw ? normaliseRunsheet(JSON.parse(raw) as Runsheet) : null;
  } catch {
    return null;
  }
}

function persist(runsheet: Runsheet): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(runsheet));
}

/** The runsheet's own fields as sent in the master PUT, for telling whether they changed. */
function masterFields(runsheet: Runsheet): string {
  const { lines: _lines, totals: _totals, ...master } = runsheet;
  return JSON.stringify(master);
}

/**
 * The lines after moving `item` from `fromIndex` to `toIndex`, mirroring
 * RunsheetService.moveItem(): from the first numbered stop on, every line is renumbered
 * in turn and marked dirty, and only lines of this run take up a number.
 */
export function reorderLines(
  lines: RunsheetLine[],
  runid: number,
  item: RunsheetLine,
  fromIndex: number,
  toIndex: number,
): RunsheetLine[] {
  const moved = [...lines];
  moved.splice(fromIndex, 1);
  moved.splice(toIndex, 0, item);

  let displayorder = 1;
  let started = false;
  for (let i = 0; i < moved.length; i++) {
    const l = moved[i]!;
    if (!started && l.displayorder > 0) started = true;
    if (!started) continue;
    moved[i] = { ...l, displayorder, dirty: true };
    if (l.runid === runid) displayorder++;
  }
  return moved;
}

interface UseRunsheetReturn {
  runsheet: Runsheet | null;
  loading: boolean;
  error: string | null;
  /** force=true: always hit API.  reload=true: discard local and use API copy. */
  fetchRunsheet: (force?: boolean, reload?: boolean) => Promise<void>;
  saveRunsheet: (updated: Runsheet) => Promise<Runsheet>;
  updateLine: (line: RunsheetLine) => Promise<void>;
  moveLine: (item: RunsheetLine, fromIndex: number, toIndex: number) => Promise<void>;
  setRunsheet: React.Dispatch<React.SetStateAction<Runsheet | null>>;
}

export function useRunsheet(): UseRunsheetReturn {
  const [runsheet, setRunsheet] = useState<Runsheet | null>(loadLocal);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Abort controller for cleanup
  const abortRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  /**
   * saveRunsheet — mirrors RunsheetService.save().
   * Persists to localStorage immediately, then fires dirty-line PUT + master PUT
   * in parallel.
   */
  const saveRunsheet = useCallback(async (updated: Runsheet): Promise<Runsheet> => {
    // The no-runsheet placeholder (id 0) isn't a real run, so never store or send it.
    if (updated.empty) return updated;

    const withTotals = { ...updated, totals: calcTotals(updated.lines ?? []) };
    persist(withTotals);

    const dirtyLines = withTotals.lines.filter(l => l.dirty);
    const promises: Promise<unknown>[] = [];

    if (dirtyLines.length > 0) {
      promises.push(axios.put(`${BASE_URL}runsheetlines`, dirtyLines));
    }

    if (withTotals.dirty) {
      const { lines: _lines, ...master } = withTotals;
      promises.push(axios.put(`${BASE_URL}runsheet`, master));
    }

    if (promises.length === 0) {
      setRunsheet(withTotals);
      return withTotals;
    }

    await Promise.all(promises);

    // The driver may have edited while the PUTs were in flight, and every edit is stored
    // first, so clear dirty on the stored lines and master that still match what was sent
    // rather than writing this older copy back. Anything edited since stays dirty.
    const stored = loadLocal();
    if (stored?.id !== withTotals.id) return withTotals;
    const sent = new Set(dirtyLines.map(l => JSON.stringify(normaliseLine(l))));
    const lines = stored.lines.map(l => (sent.has(JSON.stringify(l)) ? { ...l, dirty: false } : l));
    const synced: Runsheet = {
      ...stored,
      lines,
      dirty:
        withTotals.dirty && masterFields(stored) === masterFields(withTotals)
          ? false
          : stored.dirty,
      totals: calcTotals(lines),
    };
    persist(synced);
    setRunsheet(synced);
    return synced;
  }, []);

  const fetchRunsheet = useCallback(
    async (force = false, reload = false): Promise<void> => {
      let local = loadLocal();

      if (!force && !reload && local) {
        setRunsheet({
          ...local,
          loaded: true,
          empty: false,
          totals: calcTotals(local.lines ?? []),
        });
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data } = await axios.get<{
          success: boolean;
          runsheet: Runsheet;
          message?: string;
        }>(`${BASE_URL}runsheet`);

        if (!data.success) throw new Error(data.message ?? 'Failed to load runsheet');

        const remote = normaliseRunsheet(data.runsheet);

        // Read again, so an edit saved while the request was in flight is merged too.
        local = loadLocal();

        // Merge logic: the original RunsheetService.get() merge algorithm, except that a
        // dirty line also keeps its local copy. The original let the server overwrite an
        // offline edit that hadn't synced yet.
        if (!local || reload) {
          local = remote;
        } else if (local.id !== remote.id) {
          local = remote;
        } else if (remote.lines) {
          for (const remoteLine of remote.lines) {
            const localIdx = local.lines.findIndex(l => l.id === remoteLine.id);

            if (localIdx === -1) {
              local.lines.push(remoteLine);
            } else {
              const localLine = local.lines[localIdx]!;
              // Only a clean line takes the server copy. A dirty one is an edit the server
              // hasn't had yet.
              if (localLine.driver_locked || localLine.status === 'complete') {
                local.lines[localIdx] = { ...localLine, status: 'complete' };
              } else if (!localLine.dirty) {
                local.lines[localIdx] = remoteLine;
              }
            }
          }
        }

        if (!local.current_weight) local.current_weight = 0;

        const merged: Runsheet = {
          ...local,
          loaded: true,
          empty: false,
          totals: calcTotals(local.lines ?? []),
        };

        setRunsheet(merged);
        // saveRunsheet stores the merge. The server answered, so it also sends the edits an
        // offline save left dirty. If that fails too, they stay dirty for the next fetch.
        void saveRunsheet(merged).catch(() => undefined);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to load runsheet';
        setError(msg);
        // Fall back to whatever is in localStorage (offline-first)
        if (local) {
          setRunsheet({
            ...local,
            loaded: true,
            empty: false,
            totals: calcTotals(local.lines ?? []),
          });
        } else {
          // A complete object rather than a cast: the dashboard reads lines
          // unconditionally, and a partial placeholder crashed it.
          setRunsheet({
            id: 0,
            runid: 0,
            runnamewithdate: '',
            current_weight: 0,
            dirty: false,
            lines: [],
            totals: calcTotals([]),
            loaded: true,
            empty: true,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [saveRunsheet],
  );

  /**
   * updateLine — marks one line dirty and saves.
   * Mirrors RunsheetService.saveLine() minus the reorder logic.
   */
  const updateLine = useCallback(
    async (line: RunsheetLine): Promise<void> => {
      setRunsheet(prev => {
        if (!prev) return prev;
        const lines = prev.lines.map(l => (l.id === line.id ? { ...line, dirty: true } : l));
        const updated = { ...prev, lines, totals: calcTotals(lines) };
        persist(updated);
        // Fire-and-forget API sync. A failed sync leaves the line dirty in localStorage,
        // and the next successful fetch sends it again.
        void saveRunsheet(updated).catch(() => undefined);
        return updated;
      });
    },
    [saveRunsheet],
  );

  /**
   * moveLine — mirrors RunsheetService.moveItem(). Moves and renumbers with
   * reorderLines(), then saves.
   */
  const moveLine = useCallback(
    async (item: RunsheetLine, fromIndex: number, toIndex: number): Promise<void> => {
      setRunsheet(prev => {
        if (!prev) return prev;
        const lines = reorderLines(prev.lines, prev.runid, item, fromIndex, toIndex);
        const updated = { ...prev, lines, totals: calcTotals(lines) };
        persist(updated);
        void saveRunsheet(updated).catch(() => undefined);
        return updated;
      });
    },
    [saveRunsheet],
  );

  return {
    runsheet,
    loading,
    error,
    fetchRunsheet,
    saveRunsheet,
    updateLine,
    moveLine,
    setRunsheet,
  };
}
